/**
 * DictConsCard — карточка мини-склада расходника (screen-21 consCard
 * J:196-257, DICT-3): ленивый GET /:id/card (useDictCard), editSt-буфер
 * локальным state (копии при открытии); Название, erow2 Единица (DictUnitCtl,
 * Д7) | Стоимость «₽N / ед» → NumPadPop money «Стоимость за {u}»; ПЯТЬ
 * блоков потребителей (Д2: Квесты, Чайные, Операторы, Услуги — живые словари,
 * нормы спецверсий read-only хвостом в Квестах, Д3; 05.10 — Филиалы: общий
 * расход на час работы или на рабочий день, дробная норма); история покупок с
 * реальным медиа (DictPurchases). «Сохранить» = PUT полей + PUT bindings
 * (replace-set, спецверсии не трогаются) → onSaved у хоста (patchRow +
 * flash + refetch). Буфер правок — хостовый map p.buf (эталон: editSt —
 * module-map J:197-199): сворачивание секции/фильтр/переключение editId
 * размонтируют карточку, но правки живут — повторное открытие строки их
 * восстанавливает; буфер умирает ТОЛЬКО по «Сохранить»/«Отмена» (J:254-255).
 * При err загрузки формы привязок НЕ рендерятся (PUT по нагруженной
 * карточке перетёр бы БД) — только «Повторить»/«Отмена».
 */
import { useEffect, useState } from "react";
import type { ConsCardWire, DictConsumable, DictPurchase } from "../../api/dictTypes";
import type { Branch, MasterClass, Quest, TeaRoom } from "../../api/types";
import { NumPadPop } from "../../components/shared";
import { useDictCard } from "../../hooks/useDictCard";
import { reloadBranchDays } from "../../hooks/useBranchDays";
import { bindingsBody, initSt, sameBindings, type ConsSt } from "./consCardState";
import { ConsumersBlock, type ConsUiRow, type DictPop } from "./DictBlocks";
import { DictPurchases } from "./DictPurchases";
import { DictUnitCtl } from "./DictUnitCtl";
import { fmtNum } from "./PriceRow";
import { ActionPair } from "./QuestFormCells";
import cfg from "./config.module.css";
import qcss from "./quests.module.css";
import scss from "./services.module.css";
import css from "./dicts.module.css";

/** Роль-цели операторских норм (Д10) — UI-словарь, лейблы = серверные. */
const OP_DICT = [
  { id: "lead", label: "Каждому ведущему" },
  { id: "hall_admin", label: "Администратор смены" },
];

export type { ConsSt } from "./consCardState";

export interface DictConsCardProps {
  row: DictConsumable;
  quests: Quest[];
  teas: TeaRoom[];
  services: MasterClass[];
  /** 05.10: активные филиалы — словарь блока «Филиалы». */
  branches: Branch[];
  /** Хостовый буфер правок id → ConsSt (переживает размонтирование). */
  buf: Record<string, ConsSt | undefined>;
  onClose: () => void;
  /** use_count — пересчёт по сохранённым скоупам (вкл. read-only спецверсии):
   *  строка списка сразу видит свежий K, ✕ после сейва не промахивается
   *  веткой Д6 по стейл-счётчику (refetch подтвердит следом). */
  onSaved: (patch: { name: string; unit: string; cost: number; use_count: number }) => void;
  /** Поля сохранились, а потребители нет (ОО без прав на нормы): строка списка — с новыми полями. */
  onFields?: (patch: { name: string; unit: string; cost: number }) => void;
}

export function DictConsCard(p: DictConsCardProps) {
  const d = useDictCard("c", p.row.id);
  const [st, setSt] = useState<ConsSt | null>(() => p.buf[p.row.id] ?? null);
  const [added, setAdded] = useState<DictPurchase[]>([]);
  const [pop, setPop] = useState<DictPop | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  useEffect(() => {
    if (d.consCard) setSt((old) => old ?? initSt(d.consCard as ConsCardWire));
  }, [d.consCard]);
  /** Все правки — через set2: зеркалим в p.buf (правки переживают unmount). */
  const set2 = (v: ConsSt | ((o: ConsSt) => ConsSt)) => setSt((o) => {
    if (!o) return o;
    const nv = typeof v === "function" ? v(o) : v;
    p.buf[p.row.id] = nv;
    return nv;
  });
  /** «Отмена»/успешный сейв — единственные смерти буфера (эталон J:254-255). */
  const drop = () => { delete p.buf[p.row.id]; };
  const cancel = () => { drop(); p.onClose(); };

  const save = async () => {
    if (!st || saving || !d.consCard) return;
    setSaving(true);
    setSaveErr(null);
    const name = st.n.trim() || d.consCard.consumable.name;
    const unit = st.u.trim() || d.consCard.consumable.unit || "шт";
    /* 05.10: потребители не менялись — их PUT не шлём (ОО правит название и цену без ложного
       «не сохранилось»); причина отказа — с сервера (dictErr), а не «ошибка сети» на любой ответ */
    const eF = await d.saveFields({ name, unit, cost: st.cost });
    const eB = eF ? null : sameBindings(st, initSt(d.consCard)) ? null : await d.saveBindings(bindingsBody(st));
    setSaving(false);
    if (!eF) reloadBranchDays(); // цена и нормы филиала — в «Часах работы филиала»
    if (!eF && eB) p.onFields?.({ name, unit, cost: st.cost });
    if (!eF && !eB) {
      drop();
      const versN = d.consCard.consumers.versions.length;
      p.onSaved({
        name, unit, cost: st.cost,
        use_count: st.qs.length + versN + st.ts.length + st.os.length + st.ss.length + st.bs.length,
      });
    } else setSaveErr(eF ? `Не сохранилось: ${eF}.` : `Название и цена сохранены, а потребители — нет: ${eB}.`);
  };

  if (d.err) {
    return (
      <div className={scss.medit}>
        <div className={cfg.errHint}>Карточка не загрузилась — повторите.</div>
        <ActionPair okLabel="Повторить" onOk={d.reload} onCancel={cancel} />
      </div>
    );
  }
  if (!st) {
    return (
      <div className={scss.medit}>
        {[0, 1, 2].map((i) => <div key={i} className={`${cfg.sk} ${cfg.skTea}`} />)}
      </div>
    );
  }
  const vers = d.consCard?.consumers.versions ?? [];
  const blk = (key: "qs" | "ts" | "os" | "ss" | "bs", ok: "qO" | "tO" | "oO" | "sO" | "bO") => ({
    open: st[ok],
    onToggle: () => set2({ ...st, [ok]: !st[ok] }),
    rows: st[key],
    onChange: (rows: ConsUiRow[], opened?: boolean) =>
      set2({ ...st, [key]: rows, [ok]: opened ? true : st[ok] }),
    unit: st.u.trim() || "шт",
    openPop: setPop,
  });
  return (
    <div className={scss.medit}>
      <div className={qcss.gap2}>
        <span className={cfg.klbl}>Название</span>
        <input className={qcss.txt} value={st.n} onChange={(e) => set2({ ...st, n: e.target.value })} />
      </div>
      <div className={css.erow2}>
        <div className={css.ecell}>
          <span className={cfg.klbl}>Единица</span>
          <DictUnitCtl u={st.u} custom={st.custom} onChange={(u, custom) => set2({ ...st, u, custom })} />
        </div>
        <div className={css.ecell}>
          <span className={cfg.klbl}>Стоимость</span>
          <button type="button" className={`${cfg.teNumbtn} ${qcss.num}`}
            onClick={(e) => setPop({
              anchor: e.currentTarget, label: `Стоимость за ${st.u}`, mode: "money", value: st.cost,
              decimals: 2, /* 31.08 (владелец): цена бывает дробной (₽12.50) */
              onCommit: (v) => set2((o) => ({ ...o, cost: v || 0 })),
            })}>
            ₽{fmtNum(st.cost)} / {st.u}
          </button>
        </div>
      </div>
      {/* 10.09 (владелец): округление переехало в каждую привязку — «в одной чайной по одной
          формуле, в другой по другой». Общего поля здесь больше нет. */}
      <ConsumersBlock title="Квесты" perDef="pl" {...blk("qs", "qO")}
        dict={p.quests.map((q) => ({ id: q.id, label: q.name }))}
        extraCount={vers.length}
        extra={vers.map((v) => (
          <div key={`${v.quest_id}:${v.version_id}`} className={css.crow}
            title="Норма спецверсии — правится в карточке версии">
            <span className={css.cn}>{v.name}</span>
            <span className={`${qcss.num} ${css.vq}`}>{v.qty}</span>
            <span className={css.cu}>{st.u.trim() || "шт"}</span>
            <span className={`${css.cper} ${v.per_player ? css.hot : css.dim} ${css.ro}`}>
              {v.per_player ? "/игрок" : "/игра"}
            </span>
          </div>
        ))} />
      <ConsumersBlock title="Чайные" perDef="tea" {...blk("ts", "tO")}
        dict={p.teas.map((t) => ({ id: t.id, label: t.name }))} />
      <ConsumersBlock title="Операторы" perDef="sh" {...blk("os", "oO")} dict={OP_DICT} />
      <ConsumersBlock title="Услуги" perDef="pl" {...blk("ss", "sO")}
        dict={p.services.map((m) => ({ id: m.id, label: m.name }))} />
      {/* 05.10 (владелец): общий расход филиала — электричество на час работы, бумага на день */}
      <ConsumersBlock title="Филиалы" perDef="hr" qtyDec={2} qtyMax={9999} {...blk("bs", "bO")}
        dict={p.branches.map((b) => ({ id: b.id, label: b.name }))} />
      <DictPurchases isCons unit={st.u.trim() || "шт"}
        purchases={[...added, ...(d.consCard?.purchases ?? [])]}
        addPurchase={d.addPurchase} uploadMedia={d.uploadMedia}
        onAdded={(x) => setAdded((a) => [x, ...a])}
        onMedia={(pid, m) => setAdded((a) =>
          a.map((x) => (x.id === pid ? { ...x, media: [...x.media, ...m] } : x)))}
        openPop={setPop} />
      {saveErr ? <div className={cfg.errHint}>{saveErr}</div> : null}
      <div className={css.footPad}>
        <ActionPair okLabel={saving ? "Сохраняем…" : "Сохранить"} okDisabled={saving}
          onOk={() => void save()} onCancel={cancel} />
      </div>
      <NumPadPop
        open={!!pop} anchor={pop?.anchor ?? null} label={pop?.label} mode={pop?.mode}
        unit={pop?.unit} max={pop?.max} value={pop?.value} decimals={pop?.decimals}
        onCommit={(v) => pop?.onCommit(v)} onClose={() => setPop(null)}
      />
    </div>
  );
}
